import { RiskLevel } from '@abs/shared-types';
/**
 * Classify the current risk level based on budget consumption.
 *
 * Classification logic (in priority order):
 * 1. Critical: attendance percentage ≤ floor, or no remaining budget
 * 2. AtRisk: within 1 absence of next penalty tier
 * 3. Warning: budget consumption ≥ 75%
 * 4. Satisfactory: default
 */
export function classifyRisk(remaining, maxAbsences, attendancePercentage, attendanceFloor, _activeTier, nextTier, usedAbsences) {
    // Critical: at or past the attendance floor
    if (attendancePercentage <= attendanceFloor) {
        return RiskLevel.Critical;
    }
    // Critical: no remaining budget (and there is a budget to exhaust)
    if (maxAbsences > 0 && remaining === 0) {
        return RiskLevel.Critical;
    }
    // Critical: max absences is 0 and any absence used
    if (maxAbsences === 0 && usedAbsences > 0) {
        return RiskLevel.Critical;
    }
    // AtRisk: within 1 absence of next penalty tier
    if (nextTier !== null) {
        const absencesToNext = nextTier.absenceThreshold - usedAbsences;
        if (absencesToNext <= 1) {
            return RiskLevel.AtRisk;
        }
    }
    // Warning: budget consumption ≥ 75%
    if (maxAbsences > 0) {
        const consumptionRatio = (maxAbsences - remaining) / maxAbsences;
        if (consumptionRatio >= 0.75) {
            return RiskLevel.Warning;
        }
    }
    return RiskLevel.Satisfactory;
}
/**
 * Get the CSS color variable name for a risk level.
 */
export function getRiskLevelColor(level) {
    switch (level) {
        case RiskLevel.Satisfactory:
            return 'var(--color-risk-satisfactory)';
        case RiskLevel.Warning:
            return 'var(--color-risk-warning)';
        case RiskLevel.AtRisk:
            return 'var(--color-risk-at-risk)';
        case RiskLevel.Critical:
            return 'var(--color-risk-critical)';
    }
}
/**
 * Get the semantic icon identifier for a risk level.
 * Presentation layers map these to concrete icon components (e.g. Lucide).
 */
export function getRiskLevelIcon(level) {
    switch (level) {
        case RiskLevel.Satisfactory:
            return 'check';
        case RiskLevel.Warning:
            return 'alert';
        case RiskLevel.AtRisk:
            return 'zap';
        case RiskLevel.Critical:
            return 'x';
    }
}
/**
 * Get a human-readable label for a risk level.
 */
export function getRiskLevelLabel(level) {
    switch (level) {
        case RiskLevel.Satisfactory:
            return 'Satisfactory';
        case RiskLevel.Warning:
            return 'Warning';
        case RiskLevel.AtRisk:
            return 'At Risk';
        case RiskLevel.Critical:
            return 'Critical';
    }
}
/**
 * Compute pass/fail status for a course with pass/fail grading mode.
 * Returns null if not a pass/fail course.
 */
export function computePassFailStatus(isPassFail, passThreshold, attendancePercentage) {
    if (!isPassFail)
        return null;
    const threshold = passThreshold ?? 70;
    const margin = attendancePercentage - threshold;
    return {
        isPassing: attendancePercentage >= threshold,
        currentPercentage: attendancePercentage,
        threshold,
        margin: Math.round(margin * 10) / 10,
    };
}
//# sourceMappingURL=riskAssessor.js.map